import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { asset } from "../data/portfolio";

/*
 * NativeImage — renders at the image's exact native ratio (never cropped).
 * Reserves space via w/h, preloads ~1600px ahead of the viewport, and shows a
 * soft paper placeholder until the full-resolution image decodes.
 */
export function NativeImage({ src, w, h, alt = "", className = "", eager = false, style, ...rest }) {
  const ref = useRef(null);
  const [show, setShow] = useState(eager);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const url = asset(src);
  const ratio = w && h ? `${w} / ${h}` : undefined;

  useEffect(() => {
    if (eager) { setShow(true); return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setShow(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setShow(true); io.disconnect(); }
    }, { rootMargin: "1600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [eager, src]);

  return (
    <img
      ref={ref}
      src={show ? url : undefined}
      alt={alt}
      width={w}
      height={h}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
      className={className}
      style={{
        display: failed ? "none" : "block",
        width: "100%",
        height: "auto",
        aspectRatio: ratio,
        backgroundColor: loaded ? "transparent" : "var(--paper-2)",
        opacity: loaded ? 1 : 0,
        transition: "opacity .45s ease",
        ...style,
      }}
      {...rest}
    />
  );
}

/*
 * CoverVideo — muted looping cover. Autoplays when >=50% in view, pauses when
 * out, shows the poster first. WebM then MP4 sources. Never cropped.
 */
export function CoverVideo({ cover, ratio = "16 / 9", className = "", style, hd = false, controls = false, playing: forcePlay }) {
  const ref = useRef(null);
  const [inview, setInview] = useState(false);
  const mp4 = asset(hd && cover?.hd ? cover.hd : cover?.mp4);
  const webm = asset(cover?.webm);
  const poster = asset(cover?.poster);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (forcePlay !== undefined) return; // controlled externally
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        setInview(e.isIntersecting && e.intersectionRatio >= 0.4);
      });
    }, { threshold: [0, 0.4, 0.75] });
    io.observe(el);
    return () => io.disconnect();
  }, [forcePlay]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true; // React omits the muted attribute; set it so browsers allow autoplay
    const want = forcePlay !== undefined ? forcePlay : inview;
    if (want) { el.play?.().catch(() => {}); } else { el.pause?.(); }
  }, [inview, forcePlay]);

  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="metadata"
      poster={poster}
      controls={controls}
      className={className}
      style={{ display: "block", width: "100%", height: "100%", aspectRatio: ratio, objectFit: "cover", ...style }}
    >
      {webm && <source src={webm} type="video/webm" />}
      {mp4 && <source src={mp4} type="video/mp4" />}
    </video>
  );
}

/*
 * VideoItem — case-study clip. Autoplays muted & looping (ref.muted is set
 * before play so browsers don't block it); pauses off-screen, resumes in view.
 * "In motion" clips have no controls; other clips keep controls to unmute.
 * rail=true renders at track height (width auto) for horizontal scrollers.
 */
export function VideoItem({ src, poster, w, h, controls = false, rail = false, className = "" }) {
  const ref = useRef(null);
  const ratio = w && h ? `${w} / ${h}` : "16 / 9";
  const maxW = Math.round(600 * (w && h ? w / h : 16 / 9));
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true; // required: React does not emit the muted attribute, so autoplay is blocked otherwise
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting && e.intersectionRatio >= 0.25) el.play?.().catch(() => {}); else el.pause?.(); });
    }, { threshold: [0, 0.25] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const style = rail
    ? { display: "block", height: "100%", width: "auto", objectFit: "contain", background: "#000", borderRadius: 4 }
    : { display: "block", width: "100%", maxWidth: maxW, aspectRatio: ratio, maxHeight: 600, objectFit: "contain", background: "#000", marginInline: "auto", borderRadius: 4 };
  return (
    <video
      ref={ref}
      src={asset(src)}
      poster={asset(poster)}
      autoPlay
      loop
      playsInline
      muted
      controls={controls}
      preload="metadata"
      data-testid="motion-video"
      className={className}
      style={style}
    />
  );
}

// Fullscreen lightbox for native-ratio images (keyboard ← → Esc).
export function Lightbox({ items, index, onIndex, onClose }) {
  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [index, items, onIndex, onClose]);

  if (index == null) return null;
  const it = items[index];
  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex flex-col"
      style={{ background: "rgba(13,13,18,.96)", height: "100dvh" }}
      onClick={onClose}
      data-testid="lightbox"
    >
      <div className="flex items-center justify-between page-x py-4" style={{ color: "#fff" }}>
        <span className="mono">{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span>
        <button className="mono" style={{ color: "var(--lime)" }} onClick={onClose} data-testid="lightbox-close">CLOSE ✕</button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-4 md:px-16 pb-10" onClick={(e) => e.stopPropagation()}>
        {items.length > 1 && (
          <button className="mono absolute left-3 md:left-8 top-1/2 -translate-y-1/2 flex items-center justify-center" style={{ color: "#fff", width: 48, height: 48, borderRadius: 999, background: "rgba(255,255,255,.14)" }}
            onClick={() => onIndex((index - 1 + items.length) % items.length)} aria-label="Previous">←</button>
        )}
        <img
          key={it.src}
          src={asset(it.src)}
          alt=""
          style={{ maxHeight: "calc(100dvh - 120px)", maxWidth: "100%", width: "auto", height: "auto", objectFit: "contain" }}
        />
        {items.length > 1 && (
          <button className="mono absolute right-3 md:right-8 top-1/2 -translate-y-1/2 flex items-center justify-center" style={{ color: "#fff", width: 48, height: 48, borderRadius: 999, background: "rgba(255,255,255,.14)" }}
            onClick={() => onIndex((index + 1) % items.length)} aria-label="Next">→</button>
        )}
      </div>
    </div>,
    document.body
  );
}
